import React, { useCallback, useMemo, useState } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import {
  Bar, CartesianGrid, Cell, ComposedChart, Line, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis
} from 'recharts';
import {
  AXIS_TICK, BORDER, ChartCard, ChartSkeleton, clip, NoData, SeriesLegend, SeriesToggle, ShareBar, Stat, StatStrip, SubText, TABLE_SX,
  TipBox, TipRow, TooltipInjected, tooltipPoint
} from '../../OnlineJournal/components/analytics/chartKit';
import { CHART } from '../../OnlineJournal/constants';
import { fmtInt, fmtPct, share } from '../../OnlineJournal/format';
import { useLoad } from '../../OnlineJournal/hooks/useLoad';
import { statusColor } from '../constants';
import { analyticsApi } from './analyticsApi';
import { fmtHours, KIND_COLORS, KIND_LABELS, kindPatch } from './analyticsFormat';
import { HEIGHT, toggleIn } from './analyticsKit';
import { LeadKindStats, LeadStatusStats, LeadtimeResponse, ViewProps } from './analyticsTypes';

type SpreadKind = 'direct' | 'via' | 'return';

interface SpreadPoint {
  key: string;
  title: string;
  direct: number;
  via: number;
  return: number;
  total: number;
  /** Доля завершённых документов, уложившихся в эту корзину и раньше, % */
  cumShare: number;
}

const SPREAD_KINDS: SpreadKind[] = ['direct', 'via', 'return'];
const SPREAD_SERIES: SeriesToggle[] = SPREAD_KINDS
  .map<SeriesToggle>(k => ({ key: k, label: KIND_LABELS[k], color: KIND_COLORS[k] }))
  .concat([{ key: 'cumShare', label: 'Накопительная доля (правая ось)', color: CHART.cum, line: true }]);

const SpreadTip: React.FC<TooltipInjected<SpreadPoint> & { completed?: number }> = props => {
  const p = tooltipPoint(props);
  if (!p) return null;
  const rows: TipRow[] = [
    { label: 'Завершено за этот срок', value: fmtInt(p.total), bold: true, hint: props.completed ? `${fmtPct(share(p.total, props.completed))} завершённых` : undefined },
    ...SPREAD_KINDS.map(k => ({ label: KIND_LABELS[k], value: fmtInt(p[k]), color: KIND_COLORS[k] })),
    { label: 'Уложились в этот срок', value: fmtPct(p.cumShare), color: CHART.cum, divider: true }
  ];
  return <TipBox width={270} title={`До «Завершен»: ${p.title}`} rows={rows} />;
};

/** Распределение времени до «Завершен» и сравнение видов документов */
const LeadChart: React.FC<{ data: LeadtimeResponse; onCohort: ViewProps['onCohort'] }> = ({ data, onCohort }) => {
  const [hidden, setHidden] = useState<string[]>([]);
  const toggle = useCallback((key: string) => setHidden(h => toggleIn(h, key)), []);
  const show = (key: string) => hidden.indexOf(key) === -1;
  const all = data.kinds.find(k => k.kind === 'all');
  const byKind = useMemo(() => new Map(data.kinds.map(k => [k.kind, k])), [data.kinds]);

  const points = useMemo<SpreadPoint[]>(() => {
    const completed = all ? all.completed : 0;
    let cum = 0;
    return data.buckets.map(b => {
      const row = { direct: 0, via: 0, return: 0 };
      SPREAD_KINDS.forEach(k => { const s = byKind.get(k); row[k] = s ? s.spread[b.key] : 0; });
      const total = row.direct + row.via + row.return;
      cum += total;
      return { key: b.key, title: b.title, ...row, total, cumShare: share(cum, completed) };
    });
  }, [data.buckets, byKind, all]);

  const stats = useMemo<Stat[]>(() => {
    if (!all) return [];
    const fast = points.length ? points[0].total : 0;
    const slow = points.filter(p => p.key === 'd30' || p.key === 'more').reduce((s, p) => s + p.total, 0);
    return [
      { label: 'Создано в периоде', value: fmtInt(all.total) },
      { label: 'Дошли до «Завершен»', value: fmtInt(all.completed), color: CHART.later, hint: `${fmtPct(share(all.completed, all.total))} созданных` },
      { label: 'Медиана', value: fmtHours(all.p50Hours), hint: 'половина завершённых быстрее' },
      { label: '90% документов', value: fmtHours(all.p90Hours), hint: 'укладываются в этот срок' },
      { label: 'Среднее', value: fmtHours(all.avgHours), tooltip: 'Среднее чувствительно к единичным долгим документам; медиана показывает типичный срок' },
      { label: 'За сутки', value: fmtPct(share(fast, all.completed)), hint: `${fmtInt(fast)} документов` },
      { label: 'Дольше 15 дней', value: fmtPct(share(slow, all.completed)), color: slow ? '#b45309' : undefined, hint: `${fmtInt(slow)} документов` }
    ];
  }, [all, points]);

  if (!all || !all.total) return <NoData height={HEIGHT} text="В периоде нет документов по выбранным фильтрам" />;

  return (
    <Box>
      <StatStrip items={stats} />
      {all.completed ? (
        <>
          <SeriesLegend items={SPREAD_SERIES} hidden={hidden} onToggle={toggle} />
          <Box sx={{ height: HEIGHT - 40 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={points} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
                <CartesianGrid stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="title" tick={AXIS_TICK} axisLine={{ stroke: BORDER }} tickLine={false} interval={0} />
                <YAxis yAxisId="left" tick={AXIS_TICK} tickFormatter={v => fmtInt(Number(v))} axisLine={false} tickLine={false} width={52} allowDecimals={false} />
                <YAxis
                  yAxisId="right" orientation="right" domain={[0, 100]} tick={AXIS_TICK} tickFormatter={v => `${v}%`}
                  axisLine={false} tickLine={false} width={44} hide={!show('cumShare')}
                />
                <ChartTooltip content={<SpreadTip completed={all.completed} />} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
                {SPREAD_KINDS.map(k => (show(k) ? (
                  <Bar key={k} yAxisId="left" dataKey={k} stackId="s" fill={KIND_COLORS[k]} isAnimationActive={false} maxBarSize={72} />
                ) : null))}
                {show('cumShare') && (
                  <Line yAxisId="right" dataKey="cumShare" type="monotone" stroke={CHART.cum} strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </>
      ) : (
        <NoData height={160} text="Ни один документ периода ещё не дошёл до «Завершен»" />
      )}

      <Table size="small" sx={{ ...TABLE_SX, mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Вид документа</TableCell>
            <TableCell align="right">Создано</TableCell>
            <TableCell sx={{ minWidth: 160 }}>Дошли до «Завершен»</TableCell>
            <TableCell align="right">Медиана</TableCell>
            <TableCell align="right">90%</TableCell>
            <TableCell align="right">Среднее</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.kinds.map((k: LeadKindStats) => (
            <TableRow
              key={k.kind} hover onClick={k.total ? () => onCohort(kindPatch(k.kind)) : undefined}
              sx={{ cursor: k.total ? 'pointer' : 'default', '& td': k.kind === 'all' ? { fontWeight: 700 } : undefined }}
            >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: KIND_COLORS[k.kind], flexShrink: 0 }} />
                  {KIND_LABELS[k.kind]}
                </Box>
              </TableCell>
              <TableCell align="right">{fmtInt(k.total)}</TableCell>
              <TableCell>
                <ShareBar value={share(k.completed, k.total)} color={KIND_COLORS[k.kind]} label={fmtInt(k.completed)} />
                <SubText>{fmtPct(share(k.completed, k.total))} созданных</SubText>
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtHours(k.p50Hours)}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtHours(k.p90Hours)}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtHours(k.avgHours)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', mt: 1 }}>
        Клик по строке открывает документы этого вида, созданные в периоде.
      </Typography>
    </Box>
  );
};

const StatusTip: React.FC<TooltipInjected<LeadStatusStats>> = props => {
  const p = tooltipPoint(props);
  if (!p) return null;
  return (
    <TipBox
      width={260} title={p.name}
      rows={[
        { label: 'Среднее', value: fmtHours(p.avgHours), color: statusColor(p.status), bold: true },
        { label: 'Медиана', value: fmtHours(p.p50Hours), color: '#94a3b8' },
        { label: '90%', value: fmtHours(p.p90Hours) },
        { label: 'Интервалов', value: fmtInt(p.count), divider: true }
      ]}
      footer="Интервал - от записи статуса в истории до следующей смены статуса"
    />
  );
};

/** Сколько документы находятся в каждом статусе */
const StatusChart: React.FC<{ statuses: LeadStatusStats[] }> = ({ statuses }) => {
  if (!statuses.length) return <NoData height={200} text="Нет смен статусов у документов периода" />;
  const height = Math.max(160, statuses.length * 34 + 40);
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1, fontSize: '0.74rem', color: '#334155' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: '#3b82f6' }} />Среднее (цвет статуса)
        </Box>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: '#cbd5e1' }} />Медиана
        </Box>
      </Box>
      <Box sx={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={statuses} layout="vertical" margin={{ top: 0, right: 16, left: 4, bottom: 0 }} barCategoryGap="22%">
            <CartesianGrid stroke={CHART.grid} horizontal={false} />
            <XAxis type="number" tick={AXIS_TICK} tickFormatter={v => fmtHours(Number(v))} axisLine={{ stroke: BORDER }} tickLine={false} />
            <YAxis type="category" dataKey="name" tick={AXIS_TICK} tickFormatter={v => clip(String(v), 24)} axisLine={false} tickLine={false} width={170} interval={0} />
            <ChartTooltip content={<StatusTip />} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
            <Bar dataKey="avgHours" isAnimationActive={false} maxBarSize={14}>
              {statuses.map(s => <Cell key={s.status} fill={statusColor(s.status)} />)}
            </Bar>
            <Bar dataKey="p50Hours" fill="#cbd5e1" isAnimationActive={false} maxBarSize={14} />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      <Table size="small" sx={{ ...TABLE_SX, mt: 1.5 }}>
        <TableHead>
          <TableRow>
            <TableCell>Статус</TableCell>
            <TableCell align="right">Интервалов</TableCell>
            <TableCell align="right">Среднее</TableCell>
            <TableCell align="right">Медиана</TableCell>
            <TableCell align="right">90%</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {statuses.map(s => (
            <TableRow key={s.status}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusColor(s.status), flexShrink: 0 }} />
                  {s.name}
                </Box>
              </TableCell>
              <TableCell align="right">{fmtInt(s.count)}</TableCell>
              <TableCell align="right">{fmtHours(s.avgHours)}</TableCell>
              <TableCell align="right">{fmtHours(s.p50Hours)}</TableCell>
              <TableCell align="right">{fmtHours(s.p90Hours)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

/** Сроки обработки: от создания до «Завершен» и время в статусах (документы, созданные в периоде) */
const LeadtimeView: React.FC<ViewProps> = ({ params, paramsKey, active, reloadToken, onCohort }) => {
  const load = useLoad(
    active ? (_silent: boolean, signal: AbortSignal) => analyticsApi.leadtime(params, signal) : null,
    `leadtime|${paramsKey}`, reloadToken, 'Не удалось рассчитать сроки обработки документов'
  );
  const data = load.data;
  const error = load.error ? load.error.text : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <ChartCard
        wide title="Время до «Завершен»"
        subtitle="От создания документа до первого статуса «Завершен» по истории статусов. Документы, которые ещё не завершены, в сроки не входят."
        loading={load.loading} error={error} onRetry={load.retry}
      >
        {!data && !error && <ChartSkeleton height={HEIGHT + 160} />}
        {data && <LeadChart data={data} onCohort={onCohort} />}
      </ChartCard>
      <ChartCard
        wide title="Время в статусах"
        subtitle="Сколько документы периода находились в каждом статусе до следующей смены. Сверху - статусы, в которых документы задерживаются дольше всего."
        loading={load.loading}
      >
        {!data && !error && <ChartSkeleton height={240} />}
        {!data && error && <NoData height={160} text="Данные не загружены" />}
        {data && <StatusChart statuses={data.statuses} />}
      </ChartCard>
    </Box>
  );
};

export default React.memo(LeadtimeView);
